import Button from "components/atoms/Button";
import InputDate from "components/atoms/InputDate";
import InputSelect from "components/atoms/InputSelect";
import TableDataComponent from "components/organisms/TableData";
import moment from "moment";
import React from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { MonthOptions, YesAndNoOptions } from "utils/constant";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import ActionButton from "components/atoms/ActionButton";
import { useEffect } from "react";
import UjiSilangService from "services/UjiSilang";
import { errorToastNotif, successToastNotif } from "utils/common";
import { toast } from "react-toastify";
import InputField from "components/atoms/InputField";
import InputRadio from "components/atoms/InputRadio";
import { getInterpretasi, maskNumber } from "utils/helpers";
import { CheckSquare, XSquare } from "react-feather";

const ujiSilangService = new UjiSilangService();

const Table = () => {
  const [filterState, setFilterState] = useState({
    filter_year: "",
    filter_month: "",
    filter_name: "",
  });
  const [defaultData, setDefaultData] = useState([]);
  const [dataState, setDataState] = useState({
    page: 1,
    limit: 10,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [petugasOptions, setPetugasOptions] = useState([]);

  const { currentUser } = useSelector((state) => state.authReducer);
  const provinceId = currentUser?.province_id;

  const fetchPetugas = async () => {
    try {
      const response = await ujiSilangService.petugasCrosschecker({
        page: 1,
        limit: 1000,
        searchBy: `kd_prop`,
        searchValue: `${provinceId?.split("|")[0]}`,
      });
      setPetugasOptions(
        (response.petugas ?? []).map((o) => ({
          label: `${o.name} - ${maskNumber(o.nik)}`,
          value: `${o.name} - ${o.nik}`,
        }))
      );
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPetugas();
  }, []);

  const fetchInputData = async () => {
    if (!filterState.filter_month) return;
    setIsLoading(true);
    try {
      const { filter_month, filter_year, filter_name } = filterState;
      const response = await ujiSilangService.slideGetAll({
        page: dataState.page,
        limit: dataState.limit,
        searchBy: `province_id,year,month,slide,name,is_uji_silang_prov`,
        searchValue: `${provinceId},${filter_year},${filter_month},positif,${filter_name},Ya`,
      });

      const data = (response.slide || []).map((o) => ({
        ...o,
        isEdit: false,
        is_uji_silang_pusat: o.is_uji_silang_pusat || "Tidak",
        saved_is_uji_silang_pusat: o.is_uji_silang_pusat || "",
        saved_tgl_slide_dikirim_prov: o.tgl_slide_dikirim_prov || "",
      }));

      setDefaultData(JSON.parse(JSON.stringify(data)));
      setDataState((prevState) => ({
        ...prevState,
        data,
        limit: response.limit,
        currentPage: response.currentPage,
        totalData: response.total,
        nextPage: response.nextPage,
        previousPage: response.previousPage,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInputData();
  }, [dataState.page]);

  const onPageChange = (page) => {
    setDataState((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const onSaveData = async (rowIndex, item) => {
    let errorMessage = "";
    if (!item.is_uji_silang_pusat) {
      errorMessage = "Dikirimkan untuk uji silang ke pusat tidak boleh kosong";
    } else if (
      item.is_uji_silang_pusat === "Ya" &&
      !item.tgl_slide_dikirim_prov
    ) {
      errorMessage = "Tanggal slide dikirim harus diisi";
    } else if (
      item.is_uji_silang_pusat === "Ya" &&
      !item.petugas_crosschecker_prov
    ) {
      errorMessage = "Petugas crosschecker yang memeriksa tidak boleh kosong";
    }

    if (errorMessage) {
      return toast.error(errorMessage, {
        position: "top-right",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }

    delete item.isEdit;

    await ujiSilangService
      .slideUpdate({ ...item, slide: "positif" }, item.id)
      .then(() => {
        successToastNotif("Data berhasil diperbarui");
        setDataState((prevState) => {
          const newData = [...prevState.data];
          newData[rowIndex].isEdit = false;
          newData[rowIndex].saved_is_uji_silang_pusat = item.is_uji_silang_pusat;
          newData[rowIndex].saved_tgl_slide_dikirim_prov =
            item.tgl_slide_dikirim_prov || "";
          return { ...prevState, data: newData };
        });
      })
      .catch((err) => {
        console.log(err);
        errorToastNotif(err);
      });
  };

  const columns = [
    {
      header: "Aksi",
      accessorKey: "action",
      size: 100,
      cell: ({ row: { index, original }, table }) => {
        const oldData = defaultData.find((o) => o.id === original.id);
        return (
          <>
            <div className="button-actions">
              {original.isEdit ? (
                <>
                  <ActionButton
                    onClick={() => onSaveData(index, original)}
                    btnType={"save"}
                  />
                  <ActionButton
                    onClick={() => {
                      table.options.meta?.updateData(
                        index,
                        "isEdit",
                        false,
                        JSON.parse(JSON.stringify(oldData))
                      );
                    }}
                    btnType={"cancel"}
                  />
                </>
              ) : (
                <ActionButton
                  onClick={() => {
                    table.options.meta?.updateData(index, "isEdit", true);
                  }}
                  btnType={"edit"}
                />
              )}
            </div>
            {!original.isEdit &&
            (original.saved_is_uji_silang_pusat === "Ya" &&
            original.saved_tgl_slide_dikirim_prov ? (
              <div
                style={{
                  color: "#198754",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 8,
                }}
              >
                <CheckSquare size={18} />
                Sudah Dikirim
              </div>
            ) : original.saved_is_uji_silang_pusat === "Tidak" ? (
              <div
                style={{
                  color: "#dc3545",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 8,
                }}
              >
                <XSquare size={18} />
                Tidak Dikirim
              </div>
            ) : null)}
          </>
        );
      },
    },
    {
      header: "Fasyankes",
      accessorKey: "fasyankes_id",
      cell: ({ row }) =>
        row.original.fasyankes_id
          ? row.original.fasyankes_id?.split("|")[1]
          : "",
      size: 200,
    },
    {
      header: "Nama",
      accessorKey: "name",
      cell: ({ row }) => row.original.name || "",
    },
    {
      header: "Kode slide",
      accessorKey: "slide_code",
      cell: ({ row }) => row.original.slide_code || "",
      size: 250,
    },
    {
      header: "Hasil pemeriksaan slide fasyankes",
      accessorKey: "hasil_fasyankes",
      cell: ({ row }) =>
        row.original.parasyte ? row.original.parasyte?.join(", ") : "",
      size: 250,
    },
    {
      header: "Hasil uji silang provinsi",
      accessorKey: "hasil_prov",
      cell: ({ row }) => row.original.hasil_prov || "",
      size: 250,
    },
    {
      header: "Interpretasi hasil uji silang",
      accessorKey: "interpretasi_hasil_prov",
      cell: ({ row: { original } }) => {
        const { hasil_prov, parasyte } = original;
        return getInterpretasi(hasil_prov, parasyte || []);
      },
      size: 250,
    },
    {
      header: "Dikirimkan untuk uji silang ke pusat",
      accessorKey: "is_uji_silang_pusat",
      cell: ({ row: { index, original }, table }) => (
        <InputRadio
          name={`is_uji_silang_pusat_${index}`}
          options={YesAndNoOptions}
          selectedOption={original?.is_uji_silang_pusat}
          disabled={!original?.isEdit}
          onChange={(e) => {
            table.options.meta?.updateData(
              index,
              "is_uji_silang_pusat",
              e.target.value
            );

            if (e.target.value === "Tidak") {
              table.options.meta?.updateData(
                index,
                "tgl_slide_dikirim_prov",
                ""
              );
              table.options.meta?.updateData(
                index,
                "petugas_crosschecker_prov",
                ""
              );
            }
          }}
        />
      ),
      size: 250,
    },
    {
      header: "Tanggal slide dikirim",
      accessorKey: "tgl_slide_dikirim_prov",
      cell: ({ row: { index, original }, table }) => {
        if (original?.is_uji_silang_pusat === "Tidak") return "";
        if (!original?.isEdit) return original.tgl_slide_dikirim_prov || "";
        return (
          <InputDate
            placeholder={"Pilih tanggal"}
            name={"tgl_slide_dikirim_prov"}
            dateFormat={"YYYY-MM-DD"}
            value={original.tgl_slide_dikirim_prov ?? ""}
            onChange={(e) => {
              const selectedDate = e ? moment(e).format("YYYY-MM-DD") : "";
              table.options.meta?.updateData(
                index,
                "tgl_slide_dikirim_prov",
                selectedDate,
              );
            }}
          />
        );
      },
      size: 250,
    },
    {
      header: "Petugas crosschecker yang memeriksa",
      accessorKey: "petugas_crosschecker_prov",
      cell: ({ row: { index, original }, table }) => {
        if (original?.is_uji_silang_pusat === "Tidak") return "";
        return (
          <InputSelect
            name={"petugas_crosschecker_prov"}
            placeholder={"Pilih petugas"}
            data={petugasOptions}
            disabled={
              original?.is_uji_silang_pusat === "Tidak" || !original?.isEdit
            }
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              table.options.meta?.updateData(
                index,
                "petugas_crosschecker_prov",
                selectedValue
              );
            }}
            value={petugasOptions.find(
              (o) => o.value === original.petugas_crosschecker_prov
            )}
          />
        );
      },
      size: 250,
    },
  ];

  const tableInstance = useReactTable({
    columns,
    data: dataState?.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    meta: {
      updateData: (rowIndex, field, value, rowData) => {
        setDataState((prevState) => {
          const newData = [...prevState.data];
          if (rowData) newData[rowIndex] = rowData;
          newData[rowIndex][field] = value;
          return { ...prevState, data: newData };
        });
      },
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <h1 className="page-title">Uji Silang Provinsi - Slide Positif</h1>
      {/* Filter section start */}
      <div className="card-filter">
        <div className="filter-item">
          <InputDate
            placeholder={"Pilih tahun"}
            name={"filter_year"}
            dateFormat={"YYYY"}
            onChange={(e) => {
              const selectedYear = moment(e).format("YYYY");
              setFilterState({
                ...filterState,
                filter_year: selectedYear,
              });
            }}
            value={filterState.filter_year}
          />
        </div>
        <div className="filter-item">
          <InputSelect
            placeholder={"Pilih bulan"}
            data={MonthOptions}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              setFilterState({
                ...filterState,
                filter_month: selectedValue,
              });
            }}
            value={MonthOptions.find(
              (item) => item.value === filterState.filter_month
            )}
          />
        </div>
        <div className="filter-item">
          <InputField
            placeholder="Masukan nama"
            value={filterState.filter_name}
            onChange={(e) => {
              setFilterState({
                ...filterState,
                filter_name: e.target.value,
              });
            }}
            name="filter_name"
          />
        </div>
        <Button
          variant={"primary"}
          size={"normal"}
          onClick={() => fetchInputData()}
        >
          Terapkan
        </Button>
      </div>
      {/* Filter section end */}

      <TableDataComponent
        columnLength={columns?.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={isLoading}
        currentPage={dataState?.currentPage}
        limit={dataState?.limit}
        nextPage={dataState?.nextPage}
        previousPage={dataState?.previousPage}
        total={dataState?.totalData}
      />
    </div>
  );
};

export default Table;
